import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Navigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createOtaRollout,
  deleteOtaRollout,
  listOtaFleetStatus,
  listOtaReleases,
  listOtaRollouts,
  deleteOtaRelease,
  revokeOtaRelease,
  setOtaReleaseRecoverySafe,
  setOtaRolloutStatus,
} from "../api/otaAdminApi";
import SearchPicker from "../components/SearchPicker";
import { OtaReleaseEditForm, OtaRolloutEditForm, parseRamp } from "../components/OtaEditForms";
import OtaUploadForm from "../components/OtaUploadForm";
import { getTenants } from "../api/tenantApi";
import { listEdges } from "../api/edgeApi";
import { formatDate } from "../utils/i18nFormat";

const emptyRollout = {
  release_id: "",
  tenant: null,
  edge: null,
  rollout_percentage: 100,
  ramp: "",
  soak_minutes: 30,
};

const searchTenants = (accessToken) => async (q) => {
  const res = await getTenants(accessToken, { q: q || undefined, limit: 20 });
  return (res.items || res).map((x) => ({ id: x.id, label: x.name || x.id }));
};
const searchEdges = (accessToken, tenantId) => async (q) => {
  const { items } = await listEdges(accessToken, { q: q || undefined, tenant_id: tenantId || undefined, limit: 20 });
  return items.map((e) => ({ id: e.id, label: e.name || e.edgeId }));
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
};
const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid var(--border)",
  color: "var(--text-muted)",
  fontWeight: 600,
};
const tdStyle = {
  padding: "10px 12px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};
const cardStyle = {
  display: "grid",
  gap: 14,
  maxWidth: 960,
  padding: 20,
  border: "1px solid var(--border)",
};
const fieldStyle = { display: "grid", gap: 6 };

function scopeLabel(t, item) {
  if (item.edge_id) return t("admin.ota.scope.edge", { name: item.edge_name || item.edge_id });
  if (item.tenant_id) return t("admin.ota.scope.tenant", { name: item.tenant_name || item.tenant_id });
  return t("admin.ota.scope.global");
}

function updateStateLabel(t, state) {
  if (state === "up_to_date" || state === "pending") return t(`admin.ota.updateState.${state}`);
  return t("admin.ota.updateState.unknown");
}

// Debounces a search box into a `q` value, resetting to page 0 on change.
function useSearch(setPage) {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setQ(input.trim());
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);
  return [input, setInput, q];
}

/** Manage signed OTA metadata, rollouts, and fleet OS status. */
const TABS = ["releases", "rollouts", "devices"];
const statusLabel = (t, status) => t(`admin.ota.status.${status}`, { defaultValue: status });

export default function OtaManagement() {
  const { t } = useTranslation();
  const { tab } = useParams();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const [releases, setReleases] = useState([]);
  const [rollouts, setRollouts] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [rollout, setRollout] = useState(emptyRollout);
  const [busy, setBusy] = useState(false);
  const PAGE_SIZE = 25;
  const [releasePage, setReleasePage] = useState(0);
  const [releaseTotal, setReleaseTotal] = useState(0);
  const [tenantFilter, setTenantFilter] = useState(null);
  const [rolloutPage, setRolloutPage] = useState(0);
  const [rolloutTotal, setRolloutTotal] = useState(0);
  const [fleetPage, setFleetPage] = useState(0);
  const [fleetTotal, setFleetTotal] = useState(0);
  const [releaseSearchInput, setReleaseSearchInput, releaseSearch] = useSearch(setReleasePage);
  const [rolloutSearchInput, setRolloutSearchInput, rolloutSearch] = useSearch(setRolloutPage);
  const [fleetSearchInput, setFleetSearchInput, fleetSearch] = useSearch(setFleetPage);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    const [releaseResponse, rolloutResponse, fleetResponse] = await Promise.all([
      listOtaReleases(accessToken, {
        limit: PAGE_SIZE,
        offset: releasePage * PAGE_SIZE,
        q: releaseSearch || undefined,
      }),
      listOtaRollouts(accessToken, {
        limit: PAGE_SIZE,
        offset: rolloutPage * PAGE_SIZE,
        q: rolloutSearch || undefined,
        tenant_id: tenantFilter?.id,
      }),
      listOtaFleetStatus(accessToken, {
        limit: PAGE_SIZE,
        offset: fleetPage * PAGE_SIZE,
        q: fleetSearch || undefined,
        tenant_id: tenantFilter?.id,
      }),
    ]);
    setReleases(releaseResponse.items);
    setReleaseTotal(releaseResponse.total);
    setRollouts(rolloutResponse.items);
    setRolloutTotal(rolloutResponse.total);
    setFleet(fleetResponse.items);
    setFleetTotal(fleetResponse.total);
  }, [accessToken, releasePage, rolloutPage, fleetPage, releaseSearch, rolloutSearch, fleetSearch, tenantFilter]);

  useEffect(() => {
    refresh().catch(() => toast.error(t("admin.ota.loadFailed")));
  }, [refresh, t]);

  const startRollout = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await createOtaRollout(accessToken, {
        release_id: rollout.release_id,
        tenant_id: rollout.edge ? null : rollout.tenant?.id || null,
        edge_id: rollout.edge?.id || null,
        rollout_percentage: Number(rollout.rollout_percentage),
        ramp_steps: parseRamp(rollout.ramp),
        soak_minutes: Number(rollout.soak_minutes),
      });
      setRollout(emptyRollout);
      await refresh();
      toast.success(t("admin.ota.rolloutSaved"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.ota.rolloutFailed"));
    } finally {
      setBusy(false);
    }
  };

  const toggleRollout = async (item) => {
    const nextStatus = item.status === "active" ? "paused" : "active";
    await setOtaRolloutStatus(accessToken, item.id, { status: nextStatus });
    await refresh();
  };

  const revokeRelease = async (item) => {
    // Releases are immutable (audit trail, devices may already record "applied"),
    // so there is no hard delete -- revoke pauses every active rollout on it in
    // one backend transaction and it stops being offered to any edge.
    if (!window.confirm(t("admin.ota.revokeConfirm", { version: item.version }))) return;
    setBusy(true);
    try {
      await revokeOtaRelease(accessToken, item.id);
      await refresh();
      toast.success(t("admin.ota.revoked", { version: item.version }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.ota.revokeFailed"));
    } finally {
      setBusy(false);
    }
  };

  const deleteRelease = async (item) => {
    if (!window.confirm(t("admin.ota.deleteConfirm", { version: item.version }))) return;
    setBusy(true);
    try {
      await deleteOtaRelease(accessToken, item.id);
      await refresh();
      toast.success(t("admin.ota.deleted", { version: item.version }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.ota.deleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  const [editingRelease, setEditingRelease] = useState(null);
  const [editingRollout, setEditingRollout] = useState(null);

  const toggleRecoverySafe = async (item) => {
    setBusy(true);
    try {
      await setOtaReleaseRecoverySafe(accessToken, item.id, !item.recovery_safe);
      await refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.ota.recoveryFlagFailed"));
    } finally {
      setBusy(false);
    }
  };

  const removeRollout = async (item) => {
    const label = `${item.release_version || item.release_id} · ${scopeLabel(t, item)}`;
    if (!window.confirm(t("admin.ota.rolloutDeleteConfirm", { label }))) return;
    await deleteOtaRollout(accessToken, item.id);
    await refresh();
    toast.success(t("admin.ota.rolloutDeleted"));
  };

  const pager = (page, setPage, total) => (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
        {t("admin.ota.prev")}
      </button>
      <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
        {total === 0 ? "0" : `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, total)}`} / {total}
      </span>
      <button type="button" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
        {t("admin.ota.next")}
      </button>
    </div>
  );

  if (!TABS.includes(tab)) return <Navigate to="/ota-management/releases" replace />;

  return (
    <div style={{ display: "grid", gap: 28 }}>
      <header>
        <h1>{t("admin.ota.title")}</h1>
        <p>{t("admin.ota.intro")}</p>
      </header>
      <nav style={{ display: "flex", gap: 8 }}>
        {TABS.map((key) => (
          <NavLink key={key} to={`/ota-management/${key}`} style={({ isActive }) => ({ fontWeight: isActive ? 700 : 400, padding: "6px 14px", borderBottom: isActive ? "2px solid currentColor" : "2px solid transparent" })}>
            {t(`admin.ota.tabs.${key}`)}
          </NavLink>
        ))}
      </nav>

      {tab === "releases" && (
        <>
      <OtaUploadForm accessToken={accessToken} cardStyle={cardStyle} onDone={refresh} />

      <section style={cardStyle}>
        <h2>{t("admin.ota.releases.title")}</h2>
        <input
          placeholder={t("admin.ota.releases.search")}
          value={releaseSearchInput}
          onChange={(e) => setReleaseSearchInput(e.target.value)}
        />
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t("admin.ota.releases.col.version")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.channel")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.from")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.status")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.published")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {releases.map((item) => (
              <React.Fragment key={item.id}>
              <tr>
                <td style={tdStyle}>
                  <code>{item.version}</code>
                </td>
                <td style={tdStyle}>{item.channel}</td>
                <td style={tdStyle}>{item.from_version}</td>
                <td style={tdStyle}>{statusLabel(t, item.status)}</td>
                <td style={tdStyle}>
                  {item.published_at ? formatDate(item.published_at) : "-"}
                </td>
                <td style={{ ...tdStyle, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" disabled={busy} onClick={() => setEditingRelease(item.id)}>
                    {t("admin.ota.releases.edit")}
                  </button>
                  <button type="button" disabled={busy} onClick={() => toggleRecoverySafe(item)}>
                    {item.recovery_safe ? t("admin.ota.releases.recoveryRemove") : t("admin.ota.releases.recoveryAdd")}
                  </button>
                  {item.status === "published" && (
                    <button type="button" disabled={busy} onClick={() => revokeRelease(item)}>
                      {t("admin.ota.releases.revoke")}
                    </button>
                  )}
                  {item.status === "revoked" && (
                    <button type="button" disabled={busy} onClick={() => deleteRelease(item)}>
                      {t("admin.ota.releases.delete")}
                    </button>
                  )}
                </td>
              </tr>
              {editingRelease === item.id && (
                <tr>
                  <td style={tdStyle} colSpan={6}>
                    <OtaReleaseEditForm
                      accessToken={accessToken}
                      release={item}
                      onDone={() => { setEditingRelease(null); refresh(); }}
                      onCancel={() => setEditingRelease(null)}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {pager(releasePage, setReleasePage, releaseTotal)}
      </section>
        </>
      )}

      {tab === "rollouts" && (
        <>
      <form onSubmit={startRollout} style={cardStyle}>
        <h2>{t("admin.ota.rollouts.formTitle")}</h2>
        <p style={{ margin: 0, color: "var(--text-muted)" }}>{t("admin.ota.rollouts.formHelp")}</p>
        <label style={fieldStyle}>
          {t("admin.ota.rollouts.release")}
          <select
            required
            value={rollout.release_id}
            onChange={(e) => setRollout({ ...rollout, release_id: e.target.value })}
          >
            <option value="">{t("admin.ota.rollouts.choose")}</option>
            {releases
              .filter((item) => item.status === "published")
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.version} · {item.channel}
                </option>
              ))}
          </select>
        </label>
        <SearchPicker
          label={t("admin.ota.rollouts.tenant")}
          placeholder={t("admin.ota.rollouts.tenantSearch")}
          value={rollout.tenant}
          onChange={(tenant) => setRollout({ ...rollout, tenant, edge: null })}
          fetchItems={searchTenants(accessToken)}
        />
        <SearchPicker
          label={rollout.tenant ? t("admin.ota.rollouts.deviceInTenant", { tenant: rollout.tenant.label }) : t("admin.ota.rollouts.device")}
          placeholder={t("admin.ota.rollouts.deviceSearch")}
          value={rollout.edge}
          onChange={(edge) => setRollout({ ...rollout, edge })}
          fetchItems={searchEdges(accessToken, rollout.tenant?.id)}
          deps={[rollout.tenant?.id]}
        />
        <label style={fieldStyle}>
          {t("admin.ota.rollouts.canary")}
          <input
            type="number"
            min="0"
            max="100"
            value={rollout.rollout_percentage}
            onChange={(e) => setRollout({ ...rollout, rollout_percentage: e.target.value })}
          />
        </label>
        <label style={fieldStyle}>
          {t("admin.ota.rollouts.rampSteps")}
          <input
            value={rollout.ramp}
            placeholder="10,50,100"
            onChange={(e) => setRollout({ ...rollout, ramp: e.target.value })}
          />
        </label>
        <label style={fieldStyle}>
          {t("admin.ota.rollouts.soakMinutes")}
          <input
            type="number"
            min="1"
            value={rollout.soak_minutes}
            onChange={(e) => setRollout({ ...rollout, soak_minutes: e.target.value })}
          />
        </label>
        <button disabled={busy} type="submit">
          {t("admin.ota.rollouts.save")}
        </button>
      </form>

      <section style={cardStyle}>
        <h2>{t("admin.ota.rollouts.title")}</h2>
        <SearchPicker
          label={t("admin.ota.rollouts.tenantFilter")}
          placeholder={t("admin.ota.rollouts.tenantSearch")}
          value={tenantFilter}
          onChange={(picked) => { setTenantFilter(picked); setRolloutPage(0); setFleetPage(0); }}
          fetchItems={searchTenants(accessToken)}
        />
        <input
          placeholder={t("admin.ota.rollouts.search")}
          value={rolloutSearchInput}
          onChange={(e) => setRolloutSearchInput(e.target.value)}
        />
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t("admin.ota.releases.col.version")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.channel")}</th>
              <th style={thStyle}>{t("admin.ota.rollouts.col.target")}</th>
              <th style={thStyle}>%</th>
              <th style={thStyle}>{t("admin.ota.releases.col.status")}</th>
              <th style={thStyle}>{t("admin.ota.rollouts.col.health")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {rollouts.map((item) => (
              <React.Fragment key={item.id}>
              <tr>
                <td style={tdStyle}>
                  <code>{item.release_version || item.release_id.slice(0, 8)}</code>
                </td>
                <td style={tdStyle}>{item.release_channel || "-"}</td>
                <td style={tdStyle}>{scopeLabel(t, item)}</td>
                <td style={tdStyle}>{item.rollout_percentage}%</td>
                <td style={tdStyle}>
                  <strong>{statusLabel(t, item.status)}</strong>
                  {item.pause_reason && (
                    <div style={{ color: "var(--text-muted)" }}>{item.pause_reason}</div>
                  )}
                </td>
                <td style={tdStyle}>
                  {t("admin.ota.rollouts.health", { ok: item.succeeded, failed: item.failed })}
                  {item.ramp_steps && (
                    <div style={{ color: "var(--text-muted)" }}>
                      {t("admin.ota.rollouts.step", {
                        n: Math.max(1, item.ramp_steps.indexOf(item.rollout_percentage) + 1),
                        total: item.ramp_steps.length,
                        steps: item.ramp_steps.join(" → "),
                      })}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={() => toggleRollout(item)}>
                    {item.status === "active" ? t("admin.ota.rollouts.pause") : t("admin.ota.rollouts.resume")}
                  </button>
                  <button type="button" onClick={() => setEditingRollout(item.id)}>
                    {t("admin.ota.releases.edit")}
                  </button>
                  <button type="button" onClick={() => removeRollout(item)}>
                    {t("admin.ota.releases.delete")}
                  </button>
                </td>
              </tr>
              {editingRollout === item.id && (
                <tr>
                  <td style={tdStyle} colSpan={6}>
                    <OtaRolloutEditForm
                      accessToken={accessToken}
                      rollout={item}
                      searchTenants={searchTenants(accessToken)}
                      searchEdges={(tenantId) => searchEdges(accessToken, tenantId)}
                      onDone={() => { setEditingRollout(null); refresh(); }}
                      onCancel={() => setEditingRollout(null)}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {pager(rolloutPage, setRolloutPage, rolloutTotal)}
      </section>
        </>
      )}

      {tab === "devices" && (
        <>
      <section style={cardStyle}>
        <h2>{t("admin.ota.devices.title")}</h2>
        <SearchPicker
          label={t("admin.ota.rollouts.tenantFilter")}
          placeholder={t("admin.ota.rollouts.tenantSearch")}
          value={tenantFilter}
          onChange={(picked) => { setTenantFilter(picked); setRolloutPage(0); setFleetPage(0); }}
          fetchItems={searchTenants(accessToken)}
        />
        <input
          placeholder={t("admin.ota.devices.search")}
          value={fleetSearchInput}
          onChange={(e) => setFleetSearchInput(e.target.value)}
        />
        <p style={{ margin: 0, color: "var(--text-muted)" }}>
          {t("admin.ota.devices.help")}
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t("admin.ota.devices.col.edge")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.channel")}</th>
              <th style={thStyle}>{t("admin.ota.devices.col.reportedOs")}</th>
              <th style={thStyle}>{t("admin.ota.devices.col.target")}</th>
              <th style={thStyle}>{t("admin.ota.releases.col.status")}</th>
              <th style={thStyle}>{t("admin.ota.devices.col.lastReport")}</th>
            </tr>
          </thead>
          <tbody>
            {fleet.map((item) => (
              <tr key={item.edge_db_id}>
                <td style={tdStyle}>
                  <Link to={`/edges/${item.edge_db_id}`}>{item.name}</Link>
                </td>
                <td style={tdStyle}>{item.ota_channel}</td>
                <td style={tdStyle}>{item.reported_os_version || "—"}</td>
                <td style={tdStyle}>{item.target_version || "—"}</td>
                <td style={tdStyle}>{updateStateLabel(t, item.update_state)}</td>
                <td style={tdStyle}>
                  {item.reported_os_at
                    ? formatDate(item.reported_os_at)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pager(fleetPage, setFleetPage, fleetTotal)}
      </section>
        </>
      )}
    </div>
  );
}
